import { ReactNode } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Button, Heading, Text } from '@frontend/design-system';
import { BulkOverrideWarningAlert } from './bulk-override-warning-alert';
import { ErrorIndicator } from './error-indicator';
import { UnsavedWarningIndicator } from './unsaved-warning-indicator';

type Props = {
  title: string;
  description?: string;
  isDirty?: boolean;
  hasError?: boolean;
  onDelete?: () => void;
  onEdit?: () => void;
  children: ReactNode;
  showBulkOverrideWarningAlert?: boolean;
};

export const KnowledgeFormSection = ({
  title,
  description,
  hasError,
  isDirty,
  onDelete,
  onEdit,
  showBulkOverrideWarningAlert = false,
  children,
}: Props) => {
  const { t } = useTranslation('ai-receptionist-settings');

  return (
    <div css={styles.section}>
      <div css={styles.sectionHeader}>
        <div>
          <Heading level={3}>{title}</Heading>
          {!!description && (
            <Text color='subdued' size='medium'>
              {description}
            </Text>
          )}
        </div>
        {(hasError || isDirty || onDelete || onEdit) && (
          <div css={styles.actionsRight}>
            {hasError ? (
              <ErrorIndicator mode='inline-banner' />
            ) : (
              isDirty && <UnsavedWarningIndicator mode='inline-banner' />
            )}
            {onEdit && <Button iconName='edit' hoverLabel={t('Edit')} variant='secondary' onClick={onEdit} />}
            {onDelete && (
              <Button
                iconName='trash-small'
                color='error'
                hoverLabel={t('Delete')}
                variant='secondary'
                destructive
                onClick={onDelete}
              />
            )}
          </div>
        )}
      </div>
      {children}
      {showBulkOverrideWarningAlert && <BulkOverrideWarningAlert />}
    </div>
  );
};

const styles = {
  section: css`
    display: flex;
    flex-direction: column;
    gap: var(--fabric-space-gap-md);
    padding: var(--fabric-space-gap-lg);
    border: var(--fabric-border-width-thin) solid var(--fabric-color-accent-neutral-muted);
    border-radius: var(--fabric-radius-md);
    background-color: var(--fabric-color-surface-default);
  `,
  sectionHeader: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
  `,
  actionsRight: css`
    display: flex;
    align-items: center;
    gap: var(--fabric-space-gap-xxs);
  `,
};
